export interface Author {
    slug: string;
    name: string;
    role: string;
    bio: string;
    expertise: string[];
    linkedIn?: string;
    // Author's own site, shown as "Visit website" and listed in sameAs.
    website?: string;
    // Decorative author-page hero background; also used as og:image. Paths under /public.
    heroImage?: { desktop: string; mobile: string; width: number; height: number };
}

// Profile URLs that identify the author, for Person.sameAs.
export function authorSameAs(author: Author): string[] {
    return [author.linkedIn, author.website].filter((url): url is string => Boolean(url));
}

export const authors: Author[] = [
    {
        slug: 'praveen-jha',
        name: 'Praveen Jha',
        role: 'Former Director – AI Product Strategy, Development, Sales & Business Development, Ortem Technologies',
        bio: 'Praveen Jha was the Director of AI Product Strategy, Development, Sales & Business Development at Ortem Technologies. With deep expertise in technology consulting and enterprise sales, he helped businesses identify the right digital transformation strategies - from mobile and AI solutions to cloud-native platforms. He wrote about technology adoption, business growth, and building software partnerships that deliver real ROI. Praveen has since moved on to new opportunities; his articles here remain as he wrote them.',
        expertise: ['Business Development', 'Technology Consulting', 'Digital Transformation', 'Enterprise Sales', 'AI & Mobile Strategy'],
        linkedIn: 'https://www.linkedin.com/in/praveenjha79/',
        website: 'https://praveenjha.com/',
        heroImage: {
            desktop: '/authors/praveen-jha-hero.webp',
            mobile: '/authors/praveen-jha-hero-mobile.webp',
            width: 1672,
            height: 941,
        },
    },
    {
        slug: 'nirav-b',
        name: 'Nirav B',
        role: 'Sales Head, Ortem Technologies LLC',
        bio: 'Nirav B is the Sales Head at Ortem Technologies LLC. He works with businesses planning custom software, mobile and AI projects, helping them scope the work, choose an engagement model and plan delivery with Ortem\'s engineering team.',
        expertise: ['Enterprise Sales', 'Business Development', 'Software Engagement Models'],
        heroImage: {
            desktop: '/authors/nirav-b-hero.webp',
            mobile: '/authors/nirav-b-hero-mobile.webp',
            width: 1672,
            height: 941,
        },
    },
    {
        slug: 'mehul-parmar',
        name: 'Mehul Parmar',
        role: 'Digital Marketing Head, Ortem Technologies',
        bio: 'Mehul Parmar is the Digital Marketing Head at Ortem Technologies, leading the marketing team under the direction of Praveen Jha. A seasoned digital marketing expert with 15 years of experience and 500+ projects delivered, he specialises in SEO, SEM, SMO, Affiliate Marketing, Google Ads, and Analytics. Certified in Google Ads & Analytics, he is proficient in CMS platforms including WordPress, Shopify, Magento, and Asp.net. Mehul writes about growth marketing, search strategies, and performance campaigns for technology brands.',
        expertise: ['SEO & SEM', 'Digital Marketing Strategy', 'Google Ads & Analytics', 'Content Marketing', 'Social Media Marketing'],
        linkedIn: 'https://www.linkedin.com/in/mehulseoanalyst/',
        heroImage: {
            desktop: '/authors/mehul-parmar-hero.webp',
            mobile: '/authors/mehul-parmar-hero-mobile.webp',
            width: 1672,
            height: 941,
        },
    },
    {
        slug: 'ortem-ai-team',
        name: 'Ortem AI Research Team',
        role: 'Technology Division, Ortem Technologies',
        bio: 'The Ortem AI Research Team is a cross-functional group of ML engineers, data scientists, and software architects embedded across our product, platform, and client delivery divisions. The team researches and evaluates emerging technologies — including large language models, agentic AI systems, computer vision, and MLOps infrastructure — translating complex concepts into actionable guidance for engineering leaders and enterprise decision-makers. Each article published under this byline is the result of collaborative investigation: real-world experimentation, architecture reviews, and performance benchmarking drawn from live client projects and internal R&D initiatives. The team is committed to publishing technically rigorous, vendor-neutral content that helps organisations cut through AI hype and make confident, ROI-driven technology decisions.',
        expertise: ['Artificial Intelligence', 'Machine Learning', 'Data Engineering', 'Automation', 'NLP & Computer Vision'],
        heroImage: {
            desktop: '/authors/ortem-ai-team-hero.webp',
            mobile: '/authors/ortem-ai-team-hero-mobile.webp',
            width: 1672,
            height: 941,
        },
    },
    {
        slug: 'ravi-jadhav',
        name: 'Ravi Jadhav',
        role: 'Technical Lead, Ortem Technologies',
        bio: 'Ravi Jadhav is a Technical Lead at Ortem Technologies with 13+ years of experience leading development teams and managing complex software projects. He brings a deep understanding of software engineering best practices, agile methodologies, and scalable system architecture. Ravi is passionate about building high-performing engineering teams and delivering technology solutions that drive measurable results for clients across industries.',
        expertise: ['Technical Leadership', 'Project Management', 'Software Architecture', 'Agile & Scrum', 'Team Building'],
        heroImage: {
            desktop: '/authors/ravi-jadhav-hero.webp',
            mobile: '/authors/ravi-jadhav-hero-mobile.webp',
            width: 1672,
            height: 712,
        },
    },
    {
        slug: 'ortem-team',
        name: 'Ortem Team',
        role: 'Editorial Team, Ortem Technologies',
        bio: 'The Ortem Technologies editorial team brings together expertise from across our engineering, product, and strategy divisions to produce in-depth guides, comparisons, and best-practice articles for technology leaders and decision-makers.',
        expertise: ['Software Development', 'Web Technologies', 'eCommerce', 'Cybersecurity', 'DevOps'],
    },
];

export function getAuthorBySlug(slug: string): Author | undefined {
    return authors.find(a => a.slug === slug);
}
